import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthProvider';

interface FeatureVisibility {
  showIngredients: boolean;
  showRecipes: boolean;
  showVariants: boolean;
  showMaterials: boolean;
  showPackages: boolean;
  showMakeOrders: boolean;
  showExternallySourced: boolean;
  /** Packaging is externally sourced (packaging_assembly is co_packer or both) — gates the
   *  package "Externally Produced" toggle. Distinct from showExternallySourced (which is the
   *  coarse any-co-packer flag used by products). */
  showExternallySourcedPackaging: boolean;
  /** Packaging is ALL co-packer (packaging_assembly === 'co_packer') — packages are always
   *  externally sourced, so the toggle is forced on with no choice. */
  allCoPackerPackaging: boolean;
  /** True if tenant is all co-packer (no in-house production) */
  allCoPacker: boolean;
  /** True if tenant has configured production settings */
  isConfigured: boolean;
}

const FeatureVisibilityContext = createContext<FeatureVisibility>({
  showIngredients: true,
  showRecipes: true,
  showVariants: true,
  showMaterials: true,
  showPackages: true,
  showMakeOrders: true,
  showExternallySourced: true,
  showExternallySourcedPackaging: true,
  allCoPackerPackaging: false,
  allCoPacker: false,
  isConfigured: false,
});

export const useFeatureVisibility = () => useContext(FeatureVisibilityContext);

export const FeatureVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant } = useAuth();

  const visibility = useMemo<FeatureVisibility>(() => {
    const productAssembly = tenant?.product_assembly ?? null;
    const packagingAssembly = tenant?.packaging_assembly ?? null;

    // If not configured yet, show everything (default)
    if (!productAssembly || !packagingAssembly) {
      return {
        showIngredients: true,
        showRecipes: true,
        showVariants: true,
        showMaterials: true,
        showPackages: true,
        showMakeOrders: true,
        showExternallySourced: true,
        showExternallySourcedPackaging: true,
        allCoPackerPackaging: false,
        allCoPacker: false,
        isConfigured: false,
      };
    }

    const productInHouse = productAssembly === 'in_house' || productAssembly === 'both';
    const packagingInHouse = packagingAssembly === 'in_house' || packagingAssembly === 'both';
    const anyInHouse = productInHouse || packagingInHouse;
    const anyCoPacker = productAssembly === 'co_packer' || productAssembly === 'both'
      || packagingAssembly === 'co_packer' || packagingAssembly === 'both';
    const packagingCoPacker = packagingAssembly === 'co_packer' || packagingAssembly === 'both';

    const allCoPacker = productAssembly === 'co_packer' && packagingAssembly === 'co_packer';

    return {
      showIngredients: productInHouse,
      showRecipes: productInHouse,
      showVariants: productInHouse,
      showMaterials: packagingInHouse,
      // Packages are needed whenever something is assembled in-house — products consume
      // packages (made in-house OR bought from a supplier). Hidden only for all-co-packer
      // tenants who receive finished products (packaging is inside the finished good).
      showPackages: anyInHouse,
      showMakeOrders: anyInHouse,
      showExternallySourced: anyCoPacker,
      showExternallySourcedPackaging: packagingCoPacker,
      allCoPackerPackaging: packagingAssembly === 'co_packer',
      allCoPacker,
      isConfigured: true,
    };
  }, [tenant?.product_assembly, tenant?.packaging_assembly]);

  return (
    <FeatureVisibilityContext.Provider value={visibility}>
      {children}
    </FeatureVisibilityContext.Provider>
  );
};
